import { TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE } from '../constants';
import {
  GetSelectOptionByIdFunction,
  SearchOptions,
  SelectOptionSearchFunction,
  TypesenseCollection,
} from '../types';
import { searchCollection, TypesenseDocument } from '../utils';
import {
  CurrencyCodeOption,
  type CurrencyCodeSelectOption,
  type GetCurrencyCodeSelectByIsoCodeFunction,
} from './currencyCodes.types';

export const getSearchCurrencyCodeSelectOptions: SelectOptionSearchFunction<
  CurrencyCodeSelectOption
> = async (
  searchValue: string,
  apiKey: string,
  pageIndex: number = 1,
  options: SearchOptions = {},
) => {
  const results = await searchCollection(
    TypesenseCollection.CURRENCY_CODES.name,
    'IsoCode', // Collection does not have a Name field, so we use IsoCode for searching
    searchValue,
    apiKey,
    pageIndex,
    TYPESENSE_SELECT_OPTIONS_WINDOW_SIZE,
    options.filterBy,
    options.sortBy ? `${options.sortBy}` : 'IsoCode:asc',
  );
  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch currencyCodes from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.IsoCode, // Does not have a name, so we use IsoCode as value
    id: (item?.id || '').toString(), // Collection does not have SourceId, so we use id to have a unique identifier for rendering
    isoCode: item.IsoCode, // for better clarity in usage
  }));
  return mappedResults ?? [];
};

/**
 * Retrieves a currencyCode select option by ISO code from Typesense.
 * @throws {Error} When the search fails, returns an error, or no currencyCode is found with the given ISO code
 */
export const getCurrencyCodeSelectOptionByIsoCode: GetCurrencyCodeSelectByIsoCodeFunction =
  async (
    isoCode: string,
    apiKey: string,
    additionalFilterOptions?: SearchOptions['filterBy'],
  ) => {
    const results = await searchCollection(
      TypesenseCollection.CURRENCY_CODES.name,
      'IsoCode', // Collection does not have a Name field, so we use IsoCode for searching
      '*',
      apiKey,
      1,
      1,
      additionalFilterOptions
        ? `${additionalFilterOptions} && IsoCode:=${isoCode}`
        : `IsoCode:=${isoCode}`,
    );

    if (!results.success || results.error) {
      throw new Error(
        results.error || 'Failed to fetch currencyCode by id from Typesense',
      );
    }

    const mappedResults = results?.documents?.map(
      (item: TypesenseDocument) => ({
        value: item.IsoCode, // Does not have a name, so we use IsoCode as value
        id: (item?.id || '').toString(), // Collection does not have SourceId, so we use id to have a unique identifier for rendering
        isoCode: item.IsoCode, // for better clarity in usage
      }),
    );

    if (!mappedResults || mappedResults.length === 0) {
      //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
      console.debug(`CurrencyCode with iso code ${isoCode} not found`);
      throw new Error(`CurrencyCode with iso code ${isoCode} not found`);
    }

    return mappedResults[0];
  };

/**
 * Retrieves a currencyCode select option by id from Typesense.
 * @throws {Error} When the search fails, returns an error, or no currencyCode is found with the given id
 *
 * Note: We currently only need this function to have the same signature of fetching
 * options by id as other select options, when checking in the SelectInputField if the selected value exists.
 * In other cases we use the iso code to identify currency options
 */
export const getCurrencyCodeSelectOptionById: GetSelectOptionByIdFunction<
  CurrencyCodeOption
> = async (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => {
  const results = await searchCollection(
    TypesenseCollection.CURRENCY_CODES.name,
    'IsoCode', // Collection does not have a Name field, so we use IsoCode for searching
    '*',
    apiKey,
    1,
    1,
    additionalFilterOptions
      ? `${additionalFilterOptions} && id:=${id}`
      : `id:=${id}`, // Collection does not have SourceId, so we use the actual Typesense id
  );

  if (!results.success || results.error) {
    throw new Error(
      results.error || 'Failed to fetch currencyCode by id from Typesense',
    );
  }

  const mappedResults = results?.documents?.map((item: TypesenseDocument) => ({
    value: item.IsoCode, // Does not have a name, so we use IsoCode as value
    id: (item?.id || '').toString(), // Collection does not have SourceId, so we use id to have a unique identifier for rendering
    isoCode: item.IsoCode, // for better clarity in usage
  }));

  if (!mappedResults || mappedResults.length === 0) {
    //  debug only here as we just throw an error to be able to use try-catch, but it is not necessarily an actual error
    console.debug(`CurrencyCode with id ${id} not found`);
    throw new Error(`CurrencyCode with id ${id} not found`);
  }

  return mappedResults[0];
};
